import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import {
  Loader2, Trash2, Plus, Save, Sparkles, ChevronRight,
} from 'lucide-react';
import { ReceiptViewerPanel } from '@/components/furniture/ReceiptViewerPanel';
import { fmtCurrency, fmtDate, recalcTotal, computeDistributions } from '@/lib/receiptUtils';

// ── Inline cell editor (small, table-friendly) ────────────────
function CellInput({ value, onCommit, type = 'text', placeholder = '', className = '' }) {
  const [val, setVal] = useState(value ?? '');
  useEffect(() => { setVal(value ?? ''); }, [value]);
  return (
    <Input
      type={type}
      value={val}
      onChange={e => setVal(e.target.value)}
      onBlur={() => onCommit(val)}
      onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      placeholder={placeholder}
      className={`h-8 border-0 border-b border-transparent rounded-none px-1 focus-visible:ring-0 focus-visible:border-[#2d948c]/40 ${className}`}
    />
  );
}

function TotalRow({ label, value, emphasis, muted, testId }) {
  return (
    <div className="flex items-center justify-between">
      <span className={emphasis ? 'text-[#1f2933] font-semibold' : muted ? 'text-[#5b6773]' : 'text-[#1f2933]'}>
        {label}
      </span>
      <span
        className={`tabular-nums ${emphasis ? 'text-[#1f2933] font-semibold text-base' : muted ? 'text-[#5b6773]' : 'text-[#1f2933]'}`}
        data-testid={testId}
      >
        {value}
      </span>
    </div>
  );
}

export function ReceiptReviewPanel({
  active, editable, setEditable,
  saving, onSave, onDelete, onNextAllocate,
}) {
  const commitItemField = (id, key, raw) => {
    setEditable(prev => ({
      ...prev,
      items: prev.items.map(it => {
        if (it.id !== id) return it;
        const n = ['quantity', 'unit_price', 'line_total'].includes(key)
          ? (raw === '' ? 0 : Number(raw))
          : raw;
        return recalcTotal({ ...it, [key]: n }, key);
      }),
    }));
  };

  const addRow = () => setEditable(prev => ({
    ...prev,
    items: [...prev.items, {
      id: crypto.randomUUID ? crypto.randomUUID() : String(Math.random()),
      name: '', quantity: 1, unit_price: 0, line_total: 0,
    }],
  }));

  const removeRow = (id) => setEditable(prev => ({
    ...prev,
    items: prev.items.filter(it => it.id !== id),
  }));

  const subtotal = useMemo(
    () => editable.items.reduce((s, it) => s + (Number(it.line_total) || 0), 0),
    [editable.items],
  );
  const taxNum = Number(editable.tax) || 0;
  const shipNum = Number(editable.shipping) || 0;

  const distributions = useMemo(
    () => computeDistributions({
      items: editable.items,
      taxNum, shipNum,
      shippingMode: editable.shipping_mode,
      shippingItemId: editable.shipping_item_id,
      subtotal,
    }),
    [editable.items, editable.shipping_mode, editable.shipping_item_id, taxNum, shipNum, subtotal],
  );

  const grandTotal = useMemo(
    () => editable.items.reduce((s, it) => s + (distributions[it.id]?.final || 0), 0),
    [editable.items, distributions],
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* LEFT: receipt preview */}
      <div>
        <ReceiptViewerPanel
          mode="embedded"
          receipt={active}
          title="Original receipt"
        />
      </div>

      {/* RIGHT: editable line items */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] uppercase tracking-wider text-[#5b6773] font-semibold flex items-center gap-1">
            <Sparkles className="h-3 w-3 text-[#2d948c]" /> AI-parsed line items
          </p>
          <p className="text-[10px] text-[#5b6773]">Uploaded {fmtDate(active.uploaded_at)}</p>
        </div>

        <Card className="overflow-hidden">
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_160px] gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#5b6773] font-medium">Vendor</label>
                <Input
                  value={editable.vendor}
                  onChange={e => setEditable(p => ({ ...p, vendor: e.target.value }))}
                  placeholder="e.g. West Elm"
                  data-testid="edit-vendor"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#5b6773] font-medium">Receipt total</label>
                <Input
                  value={editable.total_detected}
                  onChange={e => setEditable(p => ({ ...p, total_detected: e.target.value }))}
                  placeholder="0.00"
                  className="tabular-nums"
                  data-testid="edit-total"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#5b6773] font-medium">Note</label>
              <Input
                value={editable.note}
                onChange={e => setEditable(p => ({ ...p, note: e.target.value }))}
                placeholder="Optional"
                data-testid="edit-note"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="mt-3 overflow-hidden">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#2d948c]/[0.07]">
                    <TableHead className="text-[10px] uppercase tracking-wider text-[#1f2933]">Item</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-[#1f2933] text-right w-[70px]">Qty</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-[#1f2933] text-right w-[100px]">Unit</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-[#1f2933] text-right w-[100px]">Line</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-[#1f2933] text-right w-[90px]">Tax</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-[#1f2933] text-right w-[90px]">Ship</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-[#1f2933] text-right w-[110px]">Final</TableHead>
                    <TableHead className="w-8" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {editable.items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-sm text-[#5b6773] py-6">
                        No line items detected — add one manually below.
                      </TableCell>
                    </TableRow>
                  )}
                  {editable.items.map(it => {
                    const d = distributions[it.id] || { tax: 0, ship: 0, final: 0 };
                    return (
                      <TableRow key={it.id} className="group" data-testid={`line-item-${it.id}`}>
                        <TableCell className="py-1 min-w-[140px]">
                          <CellInput value={it.name} onCommit={v => commitItemField(it.id, 'name', v)} placeholder="Item name" />
                        </TableCell>
                        <TableCell className="py-1 text-right">
                          <CellInput type="number" value={it.quantity} onCommit={v => commitItemField(it.id, 'quantity', v)} className="text-right tabular-nums" />
                        </TableCell>
                        <TableCell className="py-1 text-right">
                          <CellInput type="number" value={it.unit_price} onCommit={v => commitItemField(it.id, 'unit_price', v)} className="text-right tabular-nums" />
                        </TableCell>
                        <TableCell className="py-1 text-right">
                          <CellInput type="number" value={it.line_total} onCommit={v => commitItemField(it.id, 'line_total', v)} className="text-right tabular-nums" />
                        </TableCell>
                        <TableCell className="py-1 text-right text-xs tabular-nums text-[#5b6773]" data-testid={`tax-share-${it.id}`}>
                          {fmtCurrency(d.tax)}
                        </TableCell>
                        <TableCell className="py-1 text-right text-xs tabular-nums text-[#5b6773]" data-testid={`ship-share-${it.id}`}>
                          {fmtCurrency(d.ship)}
                        </TableCell>
                        <TableCell className="py-1 text-right text-xs tabular-nums font-semibold text-[#1f2933]" data-testid={`final-total-${it.id}`}>
                          {fmtCurrency(d.final)}
                        </TableCell>
                        <TableCell className="py-1">
                          <Button
                            variant="ghost" size="icon"
                            className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-destructive"
                            onClick={() => removeRow(it.id)}
                            data-testid={`remove-line-${it.id}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <div className="flex items-center justify-between px-4 py-2 border-t bg-[#f3efe6]/30">
              <Button variant="ghost" size="sm" className="gap-1.5 text-[#2d948c]" onClick={addRow} data-testid="add-line-item">
                <Plus className="h-3.5 w-3.5" /> Add line
              </Button>
              <p className="text-xs tabular-nums text-[#5b6773]">
                Subtotal: <span className="font-semibold text-[#1f2933]">{fmtCurrency(subtotal)}</span>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* ── Totals: Tax + Shipping inputs + Grand total ───── */}
        <Card className="mt-3 overflow-hidden">
          <CardContent className="p-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#5b6773] font-medium">Tax</label>
                <Input
                  type="number"
                  value={editable.tax}
                  onChange={e => setEditable(p => ({ ...p, tax: e.target.value }))}
                  placeholder="0.00"
                  className="tabular-nums"
                  data-testid="edit-tax"
                />
                <p className="text-[10px] text-[#5b6773]/70 mt-1">Split proportionally across items.</p>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#5b6773] font-medium">Shipping</label>
                <Input
                  type="number"
                  value={editable.shipping}
                  onChange={e => setEditable(p => ({ ...p, shipping: e.target.value }))}
                  placeholder="0.00"
                  className="tabular-nums"
                  data-testid="edit-shipping"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#5b6773] font-medium">Shipping split</label>
                <select
                  value={editable.shipping_mode}
                  onChange={e => setEditable(p => ({ ...p, shipping_mode: e.target.value }))}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid="edit-shipping-mode"
                >
                  <option value="proportional">Proportional</option>
                  <option value="equal">Equal per item</option>
                  <option value="assigned">Assign to one item</option>
                </select>
              </div>
            </div>
            {editable.shipping_mode === 'assigned' && (
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#5b6773] font-medium">Shipping for</label>
                <select
                  value={editable.shipping_item_id || ''}
                  onChange={e => setEditable(p => ({ ...p, shipping_item_id: e.target.value || null }))}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid="edit-shipping-assigned-to"
                >
                  <option value="">— pick a line item —</option>
                  {editable.items.map(it => (
                    <option key={it.id} value={it.id}>{it.name || '(unnamed)'}</option>
                  ))}
                </select>
                {!editable.shipping_item_id && shipNum > 0 && (
                  <p className="text-[10px] text-destructive/80 mt-1">Pick the line item this shipping charge belongs to.</p>
                )}
              </div>
            )}

            <div className="border-t pt-3 space-y-1 text-sm tabular-nums">
              <TotalRow label="Subtotal" value={fmtCurrency(subtotal)} />
              <TotalRow label="Tax" value={fmtCurrency(taxNum)} muted />
              <TotalRow label="Shipping" value={fmtCurrency(shipNum)} muted />
              <div className="h-px bg-[#2d948c]/20 my-1" />
              <TotalRow
                label="Grand total"
                value={fmtCurrency(grandTotal)}
                emphasis
                testId="grand-total"
              />
              {editable.total_detected !== '' && Math.abs(grandTotal - Number(editable.total_detected)) > 0.02 && (
                <p className="text-[11px] text-amber-700/90 pt-1" data-testid="total-mismatch-warn">
                  Grand total differs from printed receipt total ({fmtCurrency(Number(editable.total_detected))}) by {fmtCurrency(Math.abs(grandTotal - Number(editable.total_detected)))}.
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="mt-4 flex items-center justify-between">
          <Button
            variant="ghost"
            className="text-destructive hover:text-destructive hover:bg-destructive/10 gap-1.5"
            onClick={() => onDelete(active.id)}
            data-testid="delete-processed-receipt"
          >
            <Trash2 className="h-4 w-4" /> Delete
          </Button>
          <div className="flex items-center gap-2">
            <Button onClick={onSave} disabled={saving} variant="outline" className="gap-1.5" data-testid="save-processed-receipt">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
            <Button
              onClick={onNextAllocate}
              disabled={saving}
              className="gap-1.5"
              data-testid="next-allocate-btn"
            >
              Next: Allocate <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
